import { ConflictDetail } from '../types/AutoScheduling';
import { AvailabilityResult, ConflictReason } from './AvailabilityService';

/**
 * Service for calculating conflict scores for schedule suggestions
 */
class SuggestionScoringService {
  /**
   * Calculate conflict score based on availability result with enhanced error handling
   * Orange conflicts = 1 point, Red conflicts = 5 points
   */
  calculateConflictScore(availabilityResult: AvailabilityResult): { 
    score: number; 
    conflicts: ConflictDetail[] 
  } {
    try {
      // Validate input
      if (!availabilityResult) {
        throw new Error('Availability result is null or undefined');
      }

      if (!Array.isArray(availabilityResult.conflictReasons)) {
        console.warn('[SuggestionScoringService] Invalid conflict reasons array, defaulting to empty array');
        return { score: 0, conflicts: [] };
      }

      let score = 0;
      const conflicts: ConflictDetail[] = [];
      const processedReasons = new Set<string>(); // Prevent duplicate conflicts

      // Process conflict reasons and calculate score
      for (let i = 0; i < availabilityResult.conflictReasons.length; i++) {
        const reason = availabilityResult.conflictReasons[i];
        
        try {
          // Validate individual conflict reason
          if (!reason) {
            console.warn(`[SuggestionScoringService] Null conflict reason at index ${i}, skipping`);
            continue;
          }

          if (!reason.severity || !reason.reason) {
            console.warn(`[SuggestionScoringService] Invalid conflict reason at index ${i}:`, reason);
            continue;
          }

          // Check for duplicate conflicts
          const reasonKey = `${reason.severity}-${reason.reason}`;
          if (processedReasons.has(reasonKey)) {
            console.debug(`[SuggestionScoringService] Duplicate conflict reason skipped: ${reasonKey}`);
            continue;
          }
          processedReasons.add(reasonKey);

          // Calculate points with validation
          const points = this.mapSeverityToPoints(reason.severity);
          if (points < 0) {
            console.warn(`[SuggestionScoringService] Invalid points calculated for severity ${reason.severity}: ${points}`);
            continue;
          }

          score += points;

          // Create conflict detail with error handling
          try {
            const conflictDetail = this.createConflictDetail(reason, points);
            conflicts.push(conflictDetail);
          } catch (detailError) {
            console.error(`[SuggestionScoringService] Error creating conflict detail for reason ${i}:`, detailError);
            // Create a fallback conflict detail
            conflicts.push({
              type: 'room',
              severity: reason.severity as 'orange' | 'red',
              description: reason.reason || 'Unknown conflict',
              points,
              affectedEntity: 'Unknown'
            });
          }

        } catch (reasonError) {
          console.error(`[SuggestionScoringService] Error processing conflict reason ${i}:`, reasonError);
          // Continue processing other reasons
        }
      }

      // Validate final score
      if (score < 0) {
        console.warn(`[SuggestionScoringService] Negative score calculated: ${score}, resetting to 0`);
        score = 0;
      }

      if (score > 1000) {
        console.warn(`[SuggestionScoringService] Unusually high score: ${score}, this may indicate an issue`);
      }

      return { score, conflicts };

    } catch (error) {
      console.error('[SuggestionScoringService] Error calculating conflict score:', error);
      
      // Return safe fallback values
      return {
        score: 999, // High score to indicate error
        conflicts: [{
          type: 'room',
          severity: 'red',
          description: 'Error analyzing conflicts for this time slot',
          points: 999,
          affectedEntity: 'System'
        }]
      };
    }
  }

  /**
   * Map severity to point values with validation
   * green = 0, orange = 1, red = 5
   */
  private mapSeverityToPoints(severity: 'green' | 'orange' | 'red'): number {
    try {
      if (!severity || typeof severity !== 'string') {
        console.warn('[SuggestionScoringService] Invalid severity value:', severity);
        return 0;
      }

      const normalizedSeverity = severity.toLowerCase().trim();
      
      switch (normalizedSeverity) {
        case 'red':
          return 5;
        case 'orange':
          return 1;
        case 'green':
          return 0;
        default:
          console.warn(`[SuggestionScoringService] Unknown severity: ${severity}, defaulting to 0 points`);
          return 0;
      }
    } catch (error) {
      console.error('[SuggestionScoringService] Error mapping severity to points:', error);
      return 0;
    }
  }

  /**
   * Create conflict details from conflict reasons with error handling
   */
  private createConflictDetails(reasons: ConflictReason[]): ConflictDetail[] {
    if (!Array.isArray(reasons)) {
      console.warn('[SuggestionScoringService] Invalid reasons array in createConflictDetails');
      return [];
    }

    const conflicts: ConflictDetail[] = [];
    
    for (let i = 0; i < reasons.length; i++) {
      try {
        const reason = reasons[i];
        if (!reason) {
          console.warn(`[SuggestionScoringService] Null reason at index ${i}`);
          continue;
        }

        const points = this.mapSeverityToPoints(reason.severity);
        const conflictDetail = this.createConflictDetail(reason, points);
        conflicts.push(conflictDetail);
      } catch (error) {
        console.error(`[SuggestionScoringService] Error creating conflict detail at index ${i}:`, error);
        // Continue processing other reasons
      }
    }

    return conflicts;
  }

  /**
   * Create a single conflict detail from a conflict reason with enhanced error handling
   */
  private createConflictDetail(reason: ConflictReason, points: number): ConflictDetail {
    try {
      // Validate input
      if (!reason) {
        throw new Error('Conflict reason is null or undefined');
      }

      if (!reason.reason || typeof reason.reason !== 'string') {
        throw new Error('Conflict reason text is missing or invalid');
      }

      if (!reason.severity) {
        throw new Error('Conflict severity is missing');
      }

      if (typeof points !== 'number' || points < 0) {
        throw new Error(`Invalid points value: ${points}`);
      }

      // Determine conflict type and affected entity from reason text
      const { type, affectedEntity } = this.parseConflictReason(reason.reason);

      // Validate severity is orange or red (green conflicts shouldn't create details)
      const validSeverities: ('orange' | 'red')[] = ['orange', 'red'];
      const normalizedSeverity = reason.severity.toLowerCase() as 'orange' | 'red';
      
      if (!validSeverities.includes(normalizedSeverity)) {
        console.warn(`[SuggestionScoringService] Unexpected severity for conflict detail: ${reason.severity}`);
      }

      return {
        type,
        severity: normalizedSeverity,
        description: reason.reason.trim(),
        points,
        affectedEntity: affectedEntity.trim()
      };

    } catch (error) {
      console.error('[SuggestionScoringService] Error creating conflict detail:', error);
      
      // Return a fallback conflict detail
      return {
        type: 'room',
        severity: 'red',
        description: reason?.reason || 'Unknown conflict',
        points: typeof points === 'number' && points >= 0 ? points : 5,
        affectedEntity: 'Unknown'
      };
    }
  }

  /**
   * Parse conflict reason text to determine type and affected entity with enhanced error handling
   */
  private parseConflictReason(reasonText: string): { 
    type: 'teacher' | 'group' | 'room' | 'holiday' | 'subject-room'; 
    affectedEntity: string 
  } {
    try {
      if (!reasonText || typeof reasonText !== 'string') {
        console.warn('[SuggestionScoringService] Invalid reason text for parsing:', reasonText);
        return { type: 'room', affectedEntity: 'Unknown' };
      }

      const lowerReason = reasonText.toLowerCase().trim();
      
      if (!lowerReason) {
        return { type: 'room', affectedEntity: 'Unknown' };
      }

      // Teacher conflicts
      if (lowerReason.includes('teacher')) {
        try {
          const match = reasonText.match(/Teacher \(([^)]+)\)/i);
          const affectedEntity = match && match[1] ? match[1].trim() : 'Unknown Teacher';
          return { type: 'teacher', affectedEntity };
        } catch (matchError) {
          console.warn('[SuggestionScoringService] Error parsing teacher from reason:', matchError);
          return { type: 'teacher', affectedEntity: 'Unknown Teacher' };
        }
      }

      // Group conflicts
      if (lowerReason.includes('group')) {
        try {
          const match = reasonText.match(/Group \(([^)]+)\)/i);
          const affectedEntity = match && match[1] ? match[1].trim() : 'Unknown Group';
          return { type: 'group', affectedEntity };
        } catch (matchError) {
          console.warn('[SuggestionScoringService] Error parsing group from reason:', matchError);
          return { type: 'group', affectedEntity: 'Unknown Group' };
        }
      }

      // Room occupancy conflicts
      if (lowerReason.includes('room') && lowerReason.includes('occupied')) {
        try {
          const match = reasonText.match(/Room \(([^)]+)\)/i);
          const affectedEntity = match && match[1] ? match[1].trim() : 'Unknown Room';
          return { type: 'room', affectedEntity };
        } catch (matchError) {
          console.warn('[SuggestionScoringService] Error parsing room from reason:', matchError);
          return { type: 'room', affectedEntity: 'Unknown Room' };
        }
      }

      // Subject-room suitability conflicts
      if (lowerReason.includes('room') && (lowerReason.includes('suitable') || lowerReason.includes('allowed'))) {
        try {
          const match = reasonText.match(/Room \(([^)]+)\)/i);
          const affectedEntity = match && match[1] ? match[1].trim() : 'Unknown Room';
          return { type: 'subject-room', affectedEntity };
        } catch (matchError) {
          console.warn('[SuggestionScoringService] Error parsing subject-room from reason:', matchError);
          return { type: 'subject-room', affectedEntity: 'Unknown Room' };
        }
      }

      // Holiday/break conflicts
      if (lowerReason.includes('holiday') || lowerReason.includes('break') || lowerReason.includes('vacation')) {
        return { type: 'holiday', affectedEntity: 'Holiday/Break' };
      }

      // Default fallback with more specific entity extraction
      try {
        // Try to extract any entity in parentheses
        const match = reasonText.match(/\(([^)]+)\)/);
        const affectedEntity = match && match[1] ? match[1].trim() : 'Unknown';
        return { type: 'room', affectedEntity };
      } catch (fallbackError) {
        console.warn('[SuggestionScoringService] Error in fallback parsing:', fallbackError);
        return { type: 'room', affectedEntity: 'Unknown' };
      }

    } catch (error) {
      console.error('[SuggestionScoringService] Error parsing conflict reason:', error);
      return { type: 'room', affectedEntity: 'Unknown' };
    }
  }
}

export default SuggestionScoringService;